import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faSpinner, faCheckCircle, faTimesCircle, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import MonteCarloResults from '../components/MonteCarloResults';
import './SpanSimulation.css';

const SpanSimulation = () => {
  // State management
  const [bridges, setBridges] = useState([]);
  const [selectedBridgeId, setSelectedBridgeId] = useState('');
  const [selectedBridge, setSelectedBridge] = useState(null); // Full bridge object with level config
  const [selectedSpanIds, setSelectedSpanIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [simulationResults, setSimulationResults] = useState(null);
  const [isSpanSelectionCollapsed, setIsSpanSelectionCollapsed] = useState(false);
  
  // Simplified hierarchical selection state
  const [selectedPath, setSelectedPath] = useState({}); // { level1: id, level2: id, direction: '...', position: '...' }
  const [hierarchyData, setHierarchyData] = useState({}); // { level1: [...], level2: [...], ... }
  const [selectedElementsCache, setSelectedElementsCache] = useState({}); // Cache for selected elements: { elementId: elementData }
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);
  const [isCalculationDetailsCollapsed, setIsCalculationDetailsCollapsed] = useState(true);
  const [spansToVisualize, setSpansToVisualize] = useState([]); // Limit chart visualization
  const [expandedSpans, setExpandedSpans] = useState(new Set()); // Track which spans are expanded
  const [isYearlyBreakdownCollapsed, setIsYearlyBreakdownCollapsed] = useState(false); // Yearly breakdown collapsed by default
  const MAX_CHART_SPANS = 10; // Maximum spans to show in chart for readability
  
  // Scenario configuration
  const [scenario, setScenario] = useState({
    timeHorizonYears: 30,
    damageSplit: { A: 70, B: 20, C: 10 },
    discountRate: 0.03,
    estimatedPaintedAreaPercent: 30
  });
  
  const [useCustomRepairSteps, setUseCustomRepairSteps] = useState(false);
  
  // Configurable decay threshold (default 2%, range 0.5%-10%+)
  const [decayThreshold, setDecayThreshold] = useState(2.0);
  
  // Configurable maximum consecutive touch-ups (default 2)
  const [maxConsecutiveTouchUps, setMaxConsecutiveTouchUps] = useState(2);
  
  // Monte Carlo configuration
  const [useMonteCarloMode, setUseMonteCarloMode] = useState(false);
  const [monteCarloConfig, setMonteCarloConfig] = useState({
    iterations: 1000,
    costVariation: 15, // ±15%
    degradationVariation: 20, // ±20%
    discountRateVariation: 1, // ±1%
    repairThreshold: 0.3, // 30% degradation
    maintenanceThreshold: 0.1 // 10% degradation
  });

  // Fetch bridges on mount
  useEffect(() => {
    fetchBridges();
  }, []);

  // Prefill scenario defaults from Project Defaults settings.
  useEffect(() => {
    const fetchProjectDefaults = async () => {
      try {
        const response = await api.get('/settings/categories');
        if (!response.data?.success) return;
        const projectDefaults = response.data?.data?.project_defaults || {};

        const estimatedAreaRaw = projectDefaults?.estimated_painted_area_pct?.value;
        const estimatedArea = estimatedAreaRaw != null ? parseFloat(estimatedAreaRaw) : null;

        const inflationRatePctRaw = projectDefaults?.default_inflation_rate_pct?.value;
        const inflationRatePct = inflationRatePctRaw != null ? parseFloat(inflationRatePctRaw) : null;

        setScenario(prev => ({
          ...prev,
          estimatedPaintedAreaPercent:
            estimatedArea != null && !Number.isNaN(estimatedArea)
              ? Math.min(100, Math.max(0, estimatedArea))
              : prev.estimatedPaintedAreaPercent,
          discountRate:
            inflationRatePct != null && !Number.isNaN(inflationRatePct)
              ? Math.min(1, Math.max(0, inflationRatePct / 100))
              : prev.discountRate
        }));
      } catch (err) {
        console.error('Error loading project defaults:', err);
      }
    };

    fetchProjectDefaults();
  }, []);

  // Reset hierarchical selections when bridge changes
  useEffect(() => {
    if (selectedBridgeId) {
      setSelectedPath({});
      setHierarchyData({});
      setSelectedSpanIds([]);
      setSelectedElementsCache({});
    }
  }, [selectedBridgeId]);

  // Fetch hierarchy data when bridge is selected
  useEffect(() => {
    const fetchHierarchy = async () => {
      if (!selectedBridgeId || !selectedBridge) return;

      try {
        setLoadingHierarchy(true);
        
        // Fetch first level (no parent)
        const hierarchyResponse = await api.get(`/span-simulation/hierarchy/${selectedBridgeId}?level=1`);
        if (hierarchyResponse.data.success) {
          setHierarchyData(prev => ({
            ...prev,
            level1: hierarchyResponse.data.data
          }));
        }
        
        // Fetch unique directions from all levels
        const directionsResponse = await api.get(`/span-simulation/directions/${selectedBridgeId}`);
        if (directionsResponse.data.success) {
          setHierarchyData(prev => ({
            ...prev,
            directions: directionsResponse.data.data
          }));
        }
        
        // Fetch unique positions from all levels (independent of direction)
        const positionsResponse = await api.get(`/span-simulation/positions/${selectedBridgeId}`);
        if (positionsResponse.data.success) {
          setHierarchyData(prev => ({
            ...prev,
            positions: positionsResponse.data.data
          }));
        }
      } catch (err) {
        console.error('Error fetching hierarchy:', err);
      } finally {
        setLoadingHierarchy(false);
      }
    };

    fetchHierarchy();
  }, [selectedBridgeId, selectedBridge]);

  // Simple: When path changes, fetch children of the highest selected level
  useEffect(() => {
    const fetchChildren = async () => {
      if (!selectedBridgeId) return;

      const enabledLevels = (() => {
        if (!selectedBridge || !selectedBridge.enabledLevels) return [1, 2, 3];
        const levels = typeof selectedBridge.enabledLevels === 'string'
          ? JSON.parse(selectedBridge.enabledLevels)
          : selectedBridge.enabledLevels;
        return Array.isArray(levels) ? levels : [1, 2, 3];
      })();
      const areaDataLevel = (() => {
        if (!selectedBridge || !selectedBridge.levelLabels) return 2;
        const levelLabels = typeof selectedBridge.levelLabels === 'string'
          ? JSON.parse(selectedBridge.levelLabels)
          : selectedBridge.levelLabels;
        for (let i = 1; i <= 8; i++) {
          const levelKey = `level${i}`;
          if (levelLabels[levelKey] && levelLabels[levelKey].holdsAreaData) {
            return i;
          }
        }
        return 2;
      })();
      
      // Find highest selected level in path (including area data level for child fetching)
      let highestLevel = 0;
      let parentId = null;
      
      // First, find highest navigation level (including area data level for child fetching)
      for (let i = 1; i <= 8; i++) {
        if (selectedPath[`level${i}`]) {
          highestLevel = i;
          parentId = selectedPath[`level${i}`];
        }
      }
      
      // Fetch direct children of highest selected level
      if (highestLevel > 0) {
        const childLevel = highestLevel + 1;
        
        // If next level is area data level, fetch it
        if (childLevel === areaDataLevel && enabledLevels.includes(areaDataLevel)) {
          try {
            setLoadingHierarchy(true);
            
            let url = `/span-simulation/hierarchy/${selectedBridgeId}?level=${areaDataLevel}`;
            if (parentId) {
              url += `&parentId=${parentId}`;
            }
            if (selectedPath.direction) {
              url += `&direction=${encodeURIComponent(selectedPath.direction)}`;
            }
            if (selectedPath.position) {
              url += `&position=${encodeURIComponent(selectedPath.position)}`;
            }
            
            const response = await api.get(url);
            if (response.data.success) {
              setHierarchyData(prev => ({
                ...prev,
                [`level${areaDataLevel}`]: response.data.data
              }));
            }
          } catch (err) {
            console.error(`Error fetching area data level ${areaDataLevel}:`, err);
          } finally {
            setLoadingHierarchy(false);
          }
        }
        // If next level is not area data level, fetch it normally
        else if (childLevel <= 8 && enabledLevels.includes(childLevel)) {
          try {
            setLoadingHierarchy(true);
            
            let url = `/span-simulation/hierarchy/${selectedBridgeId}?level=${childLevel}`;
            if (parentId) {
              url += `&parentId=${parentId}`;
            }
            if (selectedPath.direction) {
              url += `&direction=${encodeURIComponent(selectedPath.direction)}`;
            }
            if (selectedPath.position) {
              url += `&position=${encodeURIComponent(selectedPath.position)}`;
            }
            
            const response = await api.get(url);
            if (response.data.success) {
              setHierarchyData(prev => ({
                ...prev,
                [`level${childLevel}`]: response.data.data
              }));
            }
          } catch (err) {
            console.error(`Error fetching level ${childLevel}:`, err);
          } finally {
            setLoadingHierarchy(false);
          }
        }
      }
      
      // Also ensure area data level is fetched if we have a parent path to it
      // This handles cases where area data level might not be the immediate next level
      const areaDataLevelNum = areaDataLevel;
      if (areaDataLevelNum > 0 && enabledLevels.includes(areaDataLevelNum)) {
        // Find the parent of area data level
        let areaDataParentId = null;
        for (let i = areaDataLevelNum - 1; i >= 1; i--) {
          if (selectedPath[`level${i}`]) {
            areaDataParentId = selectedPath[`level${i}`];
            break;
          }
        }
        
        // If we have a parent and area data level isn't already loaded, fetch it
        if (areaDataParentId) {
          try {
            let url = `/span-simulation/hierarchy/${selectedBridgeId}?level=${areaDataLevelNum}`;
            url += `&parentId=${areaDataParentId}`;
            if (selectedPath.direction) {
              url += `&direction=${encodeURIComponent(selectedPath.direction)}`;
            }
            if (selectedPath.position) {
              url += `&position=${encodeURIComponent(selectedPath.position)}`;
            }
            
            const response = await api.get(url);
            if (response.data.success) {
              setHierarchyData(prev => ({
                ...prev,
                [`level${areaDataLevelNum}`]: response.data.data
              }));
            }
          } catch (err) {
            console.error(`Error fetching area data level ${areaDataLevelNum}:`, err);
          }
        }
      }
    };

    fetchChildren();
  }, [selectedPath, selectedBridgeId, selectedBridge]);

  // Helper to get enabled levels array
  const getEnabledLevels = () => {
    if (!selectedBridge || !selectedBridge.enabledLevels) return [1, 2, 3];
    const enabledLevels = typeof selectedBridge.enabledLevels === 'string'
      ? JSON.parse(selectedBridge.enabledLevels)
      : selectedBridge.enabledLevels;
    return Array.isArray(enabledLevels) ? enabledLevels : [1, 2, 3];
  };

  // Helper to find which level holds area data
  const getAreaDataLevel = () => {
    if (!selectedBridge || !selectedBridge.levelLabels) return 2; // Default fallback
    const levelLabels = typeof selectedBridge.levelLabels === 'string'
      ? JSON.parse(selectedBridge.levelLabels)
      : selectedBridge.levelLabels;
    
    for (let i = 1; i <= 8; i++) {
      const levelKey = `level${i}`;
      if (levelLabels[levelKey] && levelLabels[levelKey].holdsAreaData) {
        return i;
      }
    }
    return 2; // Default fallback
  };

  // Simple: Click parent → update path → fetch children
  const handleLevelSelection = (level, elementId) => {
    const areaDataLevel = getAreaDataLevel();
    
    // Don't handle area data level here - that's handled separately
    if (level === areaDataLevel) return;
    
    const newPath = { ...selectedPath };
    
    if (elementId) {
      // Set this level in path
      newPath[`level${level}`] = elementId;
      
      // Clear all levels below this one (but keep area data selections)
      for (let i = level + 1; i <= 8; i++) {
        delete newPath[`level${i}`];
        // Only clear hierarchy data, not selectedSpanIds
        const newHierarchyData = { ...hierarchyData };
        delete newHierarchyData[`level${i}`];
        setHierarchyData(newHierarchyData);
      }
    } else {
      // Deselecting - clear this and all children
      delete newPath[`level${level}`];
      for (let i = level + 1; i <= 8; i++) {
        delete newPath[`level${i}`];
        const newHierarchyData = { ...hierarchyData };
        delete newHierarchyData[`level${i}`];
        setHierarchyData(newHierarchyData);
      }
      // Clear area selections only if no parent path exists
      if (Object.keys(newPath).filter(k => k.startsWith('level')).length === 0) {
        setSelectedSpanIds([]);
      }
    }
    
    setSelectedPath(newPath);
  };

  // Simple: Toggle area data element selection (multi-select, persists across navigation)
  // Also loads child levels when an element is selected
  const handleAreaDataSelection = (elementId, isChecked) => {
    const areaDataLevel = getAreaDataLevel();
    const areaLevelKey = `level${areaDataLevel}`;
    
    if (isChecked) {
      setSelectedSpanIds(prev => {
        if (!prev.includes(elementId)) {
          return [...prev, elementId];
        }
        return prev;
      });
      
      // Cache the element data so it persists when hierarchy data is cleared
      const element = hierarchyData[areaLevelKey]?.find(el => el.id === elementId);
      if (element) {
        setSelectedElementsCache(prev => ({
          ...prev,
          [elementId]: element
        }));
      }
      
      // Also set in path to trigger child level fetch
      const newPath = { ...selectedPath };
      newPath[`level${areaDataLevel}`] = elementId;
      setSelectedPath(newPath);
    } else {
      setSelectedSpanIds(prev => prev.filter(id => id !== elementId));
      
      // Remove from cache
      setSelectedElementsCache(prev => {
        const newCache = { ...prev };
        delete newCache[elementId];
        return newCache;
      });
      
      // Remove from path if it was the tracked element
      if (selectedPath[`level${areaDataLevel}`] === elementId) {
        const newPath = { ...selectedPath };
        delete newPath[`level${areaDataLevel}`];
        setSelectedPath(newPath);
      }
    }
  };



  useEffect(() => {
    // selection summary derived from selectedSpanIds only
  }, [selectedSpanIds]);


  const fetchBridges = async () => {
    try {
      const response = await api.get('/bridges?limit=100');
      if (response.data.success) {
        setBridges(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching bridges:', err);
      setError('Failed to load bridges');
    }
  };
  
  // Fetch bridge details when selected
  useEffect(() => {
    const fetchBridgeDetails = async () => {
      if (selectedBridgeId) {
        try {
          const response = await api.get(`/bridges/${selectedBridgeId}`);
          if (response.data.success) {
            setSelectedBridge(response.data.data);
          }
        } catch (err) {
          console.error('Error fetching bridge details:', err);
        }
      } else {
        setSelectedBridge(null);
      }
    };
    fetchBridgeDetails();
  }, [selectedBridgeId]);
  
  
  // Helper to get level label
  const getLevelLabel = (levelNum) => {
    if (!selectedBridge || !selectedBridge.levelLabels) return `Level ${levelNum}`;
    const levelLabels = typeof selectedBridge.levelLabels === 'string' 
      ? JSON.parse(selectedBridge.levelLabels) 
      : selectedBridge.levelLabels;
    const levelKey = `level${levelNum}`;
    return levelLabels[levelKey]?.label || `Level ${levelNum}`;
  };
  
  // Note: We no longer fetch all spans upfront - we use hierarchical selection instead

  const handleSelectAll = async () => {
    // Select all elements at the area data level
    const areaDataLevel = getAreaDataLevel();
    try {
      // Get current area data level elements from hierarchyData
      const areaLevelKey = `level${areaDataLevel}`;
      const elements = hierarchyData[areaLevelKey] || [];
      
      if (elements.length > 0) {
        const allIds = elements.map(el => el.id);
        setSelectedSpanIds(allIds);
        setSpansToVisualize(allIds.slice(0, MAX_CHART_SPANS));
        
        // Populate cache with all element data
        const cacheUpdate = {};
        elements.forEach(element => {
          cacheUpdate[element.id] = {
            id: element.id,
            element_code: element.code || element.element_code,
            element_name: element.name || element.element_name,
            total_area: element.area || element.total_area
          };
        });
        setSelectedElementsCache(prev => ({
          ...prev,
          ...cacheUpdate
        }));
      }
    } catch (err) {
      console.error('Error selecting all:', err);
    }
  };

  const handleDeselectAll = () => {
    setSelectedSpanIds([]);
    setSpansToVisualize([]);
    setSelectedPath({});
  };

  const handleScenarioChange = (field, value) => {
    setScenario(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDamageSplitChange = (level, value) => {
    setScenario(prev => ({
      ...prev,
      damageSplit: {
        ...prev.damageSplit,
        [level]: parseInt(value)
      }
    }));
  };

  const runSimulation = async () => {
    if (selectedSpanIds.length === 0) {
      setError('Please select at least one span');
      return;
    }

    // Warn about large simulations
    if (selectedSpanIds.length > 100) {
      const confirmed = window.confirm(
        `Large Simulation Warning\n\n` +
        `You are about to simulate ${selectedSpanIds.length} elements.\n` +
        `This may take 7-10 minutes to complete.\n\n` +
        `Do you want to continue?`
      );
      if (!confirmed) {
        return;
      }
    }

    // Validate damage split totals 100%
    const totalSplit = Object.values(scenario.damageSplit).reduce((sum, val) => sum + val, 0);
    if (totalSplit !== 100) {
      setError(`Damage split must total 100% (currently ${totalSplit}%)`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSimulationResults(null);
      setExpandedSpans(new Set()); // Clear expanded spans for new simulation
      setIsYearlyBreakdownCollapsed(false); // Reset yearly breakdown to expanded for new simulation

      const response = await api.post('/span-simulation/run', {
        spanIds: selectedSpanIds,
        scenario: scenario,
        useCustomRepairSteps: useCustomRepairSteps,
        useMonteCarloMode: useMonteCarloMode,
        monteCarloConfig: useMonteCarloMode ? monteCarloConfig : null,
        decayThreshold: decayThreshold,
        maxConsecutiveTouchUps: maxConsecutiveTouchUps
      });

      if (response.data.success) {
        setSimulationResults(response.data.data);
        
        // Scroll to results
        setTimeout(() => {
          document.getElementById('simulation-results')?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }, 100);
      }
    } catch (err) {
      console.error('Error running simulation:', err);
      const msg = err.response?.data?.message || err.response?.data?.error;
      setError(msg || 'Failed to run simulation');
      const main = document.querySelector('main');
      if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  // Memoize chart data generation for better performance
  const chartData = useMemo(() => {
    const data = [];
    
    // Use spansToVisualize instead of all selectedSpanIds for better performance
    const spansForChart = spansToVisualize.slice(0, MAX_CHART_SPANS);
    
    if (spansForChart.length === 0) {
      return data;
    }
    
    // Generate data for 35 years
    for (let year = 0; year <= 35; year += 1) {
      const yearData = { year };
      
      spansForChart.forEach((spanId) => {
        // Note: We don't have span details here, using default degradation params
        // In a real scenario, we'd fetch span details or use simulation results
        
        // Get degradation parameters for this span's corrosion category
        const getDegradationParams = (corrosionCategory) => {
          const params = {
            'C2': { coefficient: 0.0098, growthRate: 0.2382 },
            'C3': { coefficient: 0.0098, growthRate: 0.3454 },
            'C4': { coefficient: 0.0111, growthRate: 0.4273 },
            'C5': { coefficient: 0.0098, growthRate: 0.4934 }
          };
          return params[corrosionCategory] || params['C3'];
        };
        
        // Use default C3 degradation params for preview
        const params = getDegradationParams('C3');
        const yearsSinceRepair = year; // Simplified for preview
        const totalYears = yearsSinceRepair;
        
        // Calculate degradation using exponential decay formula
        const degradation = Math.min(12, params.coefficient * Math.exp(params.growthRate * totalYears));
        
        yearData[`span_${spanId}`] = parseFloat(degradation.toFixed(2));
      });
      
      data.push(yearData);
    }
    
    return data;
  }, [spansToVisualize]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value, decimals = 2) => {
    return new Intl.NumberFormat('en-CA', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  };

  // Helper functions for span expansion
  const toggleSpanExpansion = (spanId) => {
    setExpandedSpans(prev => {
      const newSet = new Set(prev);
      if (newSet.has(spanId)) {
        newSet.delete(spanId);
      } else {
        newSet.add(spanId);
      }
      return newSet;
    });
  };

  const isSpanExpanded = (spanId) => {
    return expandedSpans.has(spanId);
  };

  // Helper function to get corrosion badge color
  const getCorrosionBadgeClass = (combinationCode) => {
    if (!combinationCode) return 'badge-corrosion';
    const code = combinationCode.toString();
    if (code.includes('C2')) return 'badge-corrosion-c2';
    if (code.includes('C3')) return 'badge-corrosion-c3';
    if (code.includes('C4')) return 'badge-corrosion-c4';
    if (code.includes('C5')) return 'badge-corrosion-c5';
    return 'badge-corrosion';
  };

  return (
    <div className="span-simulation-container">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Simulation Engine</h1>
        <p className="text-sm text-gray-600">Run degradation and cost simulations for bridge spans over a defined time horizon</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <FontAwesomeIcon icon={faTimesCircle} />
          {error}
        </div>
      )}

      {/* Bridge Selection */}
      <div className="section-card">
        <h2>1. Select Bridge</h2>
        <div className="form-group">
          <label>Bridge</label>
          <select
            value={selectedBridgeId}
            onChange={(e) => setSelectedBridgeId(e.target.value)}
            className="form-control"
          >
            <option value="">-- Select a bridge --</option>
            {bridges.map(bridge => (
              <option key={bridge.id} value={bridge.id}>
                {bridge.bridgeName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Span Selection */}
      {selectedBridgeId && (
        <div className="section-card">
          <div className="section-header">
            <div className="header-left">
              <button 
                className="collapse-btn"
                onClick={() => setIsSpanSelectionCollapsed(!isSpanSelectionCollapsed)}
                title={isSpanSelectionCollapsed ? 'Expand span selection' : 'Collapse span selection'}
              >
                {isSpanSelectionCollapsed ? '▶' : '▼'}
              </button>
              <h2>2. Select Elements</h2>
              {isSpanSelectionCollapsed && (
                <span className="collapsed-summary">
                  ({selectedSpanIds.length} element{selectedSpanIds.length !== 1 ? 's' : ''} selected)
                  {selectedSpanIds.length > 100 && (
                    <span style={{ color: '#f59e0b', marginLeft: '8px' }}>
                      ⚠️ Large simulation
                    </span>
                  )}
                </span>
              )}
            </div>
            {!isSpanSelectionCollapsed && (
              <div className="selection-actions">
                <button 
                  onClick={handleSelectAll} 
                  className="btn btn-secondary btn-sm"
                  disabled={!selectedBridgeId}
                >
                  Select All
                </button>
                <button 
                  onClick={handleDeselectAll} 
                  className="btn btn-secondary btn-sm"
                  disabled={selectedSpanIds.length === 0}
                >
                  Deselect All
                </button>
                <span className="selection-count">
                  {selectedSpanIds.length} element{selectedSpanIds.length !== 1 ? 's' : ''} selected
                </span>
              </div>
            )}
          </div>

          {!isSpanSelectionCollapsed && (
            <>
              <div className="card-body">
                {/* Hierarchical Cascading Selection with Listboxes */}
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Select elements through the hierarchy. Click on elements in each column to navigate. Simulation will run on elements at the level that holds area data.
                  </p>
                  
                  {/* Top Level Filters */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {/* Direction Filter */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Direction</label>
                      <select
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={selectedPath.direction || ''}
                        onChange={async (e) => {
                          const newPath = { ...selectedPath };
                          if (e.target.value) {
                            newPath.direction = e.target.value;
                          } else {
                            delete newPath.direction;
                          }
                          // Clear all level selections when direction changes
                          Object.keys(newPath).forEach(key => {
                            if (key.startsWith('level')) delete newPath[key];
                          });
                          setSelectedPath(newPath);
                          setHierarchyData({});
                          
                          // Re-fetch level 1 with new direction filter
                          if (selectedBridgeId) {
                            try {
                              setLoadingHierarchy(true);
                              let url = `/span-simulation/hierarchy/${selectedBridgeId}?level=1`;
                              if (e.target.value) {
                                url += `&direction=${encodeURIComponent(e.target.value)}`;
                              }
                              const response = await api.get(url);
                              if (response.data.success) {
                                const elements = response.data.data;
                                setHierarchyData(prev => ({
                                  ...prev,
                                  level1: elements
                                }));
                                
                                // Update positions based on filtered direction (optional - can show all positions)
                                if (e.target.value) {
                                  const positionsResponse = await api.get(
                                    `/span-simulation/positions/${selectedBridgeId}?direction=${encodeURIComponent(e.target.value)}`
                                  );
                                  if (positionsResponse.data.success) {
                                    setHierarchyData(prev => ({
                                      ...prev,
                                      positions: positionsResponse.data.data
                                    }));
                                  }
                                } else {
                                  // If direction cleared, fetch all positions
                                  const positionsResponse = await api.get(`/span-simulation/positions/${selectedBridgeId}`);
                                  if (positionsResponse.data.success) {
                                    setHierarchyData(prev => ({
                                      ...prev,
                                      positions: positionsResponse.data.data
                                    }));
                                  }
                                }
                              }
                            } catch (err) {
                              console.error('Error fetching filtered hierarchy:', err);
                            } finally {
                              setLoadingHierarchy(false);
                            }
                          }
                        }}
                      >
                        <option value="">All Directions</option>
                        {hierarchyData.directions?.map(dir => (
                          <option key={dir} value={dir}>{dir}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Position Filter */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Position</label>
                      <select
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={selectedPath.position || ''}
                        onChange={async (e) => {
                          const newPath = { ...selectedPath };
                          if (e.target.value) {
                            newPath.position = e.target.value;
                          } else {
                            delete newPath.position;
                          }
                          // Clear all level selections when position changes
                          Object.keys(newPath).forEach(key => {
                            if (key.startsWith('level')) delete newPath[key];
                          });
                          setSelectedPath(newPath);
                          setHierarchyData({});
                          
                          // Re-fetch level 1 with new position filter
                          if (selectedBridgeId) {
                            try {
                              setLoadingHierarchy(true);
                              let url = `/span-simulation/hierarchy/${selectedBridgeId}?level=1`;
                              if (newPath.direction) {
                                url += `&direction=${encodeURIComponent(newPath.direction)}`;
                              }
                              if (e.target.value) {
                                url += `&position=${encodeURIComponent(e.target.value)}`;
                              }
                              const response = await api.get(url);
                              if (response.data.success) {
                                const elements = response.data.data;
                                setHierarchyData(prev => ({
                                  ...prev,
                                  level1: elements
                                }));
                              }
                            } catch (err) {
                              console.error('Error fetching filtered hierarchy:', err);
                            } finally {
                              setLoadingHierarchy(false);
                            }
                          }
                        }}
                        disabled={false}
                      >
                        <option value="">All Positions</option>
                        {hierarchyData.positions?.map(pos => (
                          <option key={pos} value={pos}>{pos}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Hierarchical Columns - Listboxes */}
                  <div className="flex gap-2 overflow-x-auto pb-4">
                    {getEnabledLevels()
                      .filter(levelNum => levelNum <= getAreaDataLevel())
                      .map((levelNum) => {
                      const levelKey = `level${levelNum}`;
                      const levelLabel = getLevelLabel(levelNum);
                      const isAreaDataLevel = levelNum === getAreaDataLevel();
                      const elements = hierarchyData[levelKey] || [];
                      const selectedId = selectedPath[levelKey] || null;
                      
                      // Simple: Level 1 always enabled, others need parent selected
                      const isLevelEnabled = levelNum === 1 ? true : !!selectedPath[`level${levelNum - 1}`];
                      
                      return (
                        <div key={levelNum} className="flex-shrink-0" style={{ minWidth: '140px', maxWidth: '140px' }}>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {levelLabel}
                            {isAreaDataLevel && (
                              <span className="ml-1 text-xs text-blue-600 font-normal">
                                (Area)
                              </span>
                            )}
                          </label>
                          <div 
                            className="border border-gray-300 rounded-md bg-white"
                            style={{ height: '300px', overflowY: 'auto' }}
                          >
                            {loadingHierarchy && elements.length === 0 ? (
                              <div className="p-2 text-center text-gray-500 text-xs">
                                <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                                Loading...
                              </div>
                            ) : elements.length === 0 ? (
                              <div className="p-2 text-center text-gray-400 text-xs">
                                {isLevelEnabled ? 'No items' : 'Select parent'}
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-200">
                                {elements.map(element => {
                                  // For area data level, use multi-select with checkboxes
                                  if (isAreaDataLevel) {
                                    const isSelected = selectedSpanIds.includes(element.id);
                                    return (
                                      <div
                                        key={element.id}
                                        className={`p-1 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                                          isSelected ? 'bg-blue-100 border-l-2 border-blue-500' : ''
                                        }`}
                                        onClick={() => {
                                          if (isLevelEnabled) {
                                            handleAreaDataSelection(element.id, !isSelected);
                                          }
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => {}}
                                          onClick={(e) => e.stopPropagation()}
                                          className="cursor-pointer"
                                          disabled={!isLevelEnabled}
                                        />
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-xs text-gray-900 truncate" title={element.element_name}>
                                            {element.element_name}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  } else {
                                    // For navigation levels, use single selection
                                    const isSelected = selectedId === element.id;
                                    return (
                                      <div
                                        key={element.id}
                                        className={`p-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                                          isSelected ? 'bg-blue-100 border-l-2 border-blue-500' : ''
                                        }`}
                                        onClick={() => {
                                          if (isLevelEnabled) {
                                            handleLevelSelection(levelNum, isSelected ? null : element.id);
                                          }
                                        }}
                                      >
                                        <div className="font-medium text-xs text-gray-900 truncate" title={element.element_code}>
                                          {element.element_code}
                                        </div>
                                        <div className="text-xs text-gray-600 truncate" title={element.element_name}>
                                          {element.element_name}
                                        </div>
                                      </div>
                                    );
                                  }
                                })}
                              </div>
                            )}
                          </div>
                          {elements.length > 0 && (
                            <div className="mt-0.5 text-xs text-gray-500 text-center">
                              {elements.length}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Selection Summary */}
                  <div className="mt-4 bg-gray-50 border border-gray-300 rounded-md p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Selection Summary</h3>
                    <div className="space-y-2 text-sm">
                      {getEnabledLevels().map((levelNum) => {
                        const levelKey = `level${levelNum}`;
                        const levelLabel = getLevelLabel(levelNum);
                        const selectedId = selectedPath[levelKey];
                        
                        if (!selectedId) return null;
                        
                        const element = hierarchyData[levelKey]?.find(el => el.id === selectedId);
                        return (
                          <div key={levelNum} className="flex items-center justify-between">
                            <span className="text-gray-600">{levelLabel}:</span>
                            <span className="font-medium text-gray-900">
                              {element ? element.element_name : 'Selected'}
                            </span>
                          </div>
                        );
                      })}
                      
                      {selectedSpanIds.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-300">
                          <div className="text-gray-600 mb-2">Elements for Simulation:</div>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {selectedSpanIds.map(spanId => {
                              // First try to find in current hierarchy data, then in cache
                              const areaDataLevel = getAreaDataLevel();
                              const areaLevelKey = `level${areaDataLevel}`;
                              let element = hierarchyData[areaLevelKey]?.find(el => el.id === spanId);
                              
                              // If not in current hierarchy, check cache
                              if (!element) {
                                element = selectedElementsCache[spanId];
                              }
                              
                              if (!element) {
                                return (
                                  <div key={spanId} className="text-xs text-gray-500">
                                    Element ID: {spanId} (loading...)
                                  </div>
                                );
                              }
                              
                              return (
                                <div key={spanId} className="text-xs flex items-center justify-between bg-blue-50 p-2 rounded">
                                  <div>
                                    <span className="font-medium text-gray-900">
                                      {element.element_name}
                                    </span>
                                  </div>
                                  <div className="text-gray-600 ml-2">
                                    {element.total_area ? `${parseFloat(element.total_area).toFixed(2)} m²` : 'No area'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            Total: {selectedSpanIds.length} element{selectedSpanIds.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {selectedSpanIds.length > 100 && (
                      <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
                        ⚠️ Large simulation: {selectedSpanIds.length} elements selected
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}


      {/* Scenario Configuration */}
      {selectedBridgeId && selectedSpanIds.length > 0 && (
        <div className="section-card">
          <h2>3. Configure Scenario</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label>Time Horizon (years)</label>
              <input
                type="number"
                min="1"
                max="50"
                value={scenario.timeHorizonYears}
                onChange={(e) => handleScenarioChange('timeHorizonYears', parseInt(e.target.value))}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Inflation Rate (%)</label>
              <input
                type="number"
                min="0"
                max="20"
                step="0.1"
                value={scenario.discountRate * 100}
                onChange={(e) => handleScenarioChange('discountRate', parseFloat(e.target.value) / 100)}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Estimated Painted Area (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={scenario.estimatedPaintedAreaPercent}
                onChange={(e) => {
                  const parsedValue = parseFloat(e.target.value);
                  handleScenarioChange('estimatedPaintedAreaPercent', Number.isNaN(parsedValue) ? 0 : parsedValue);
                }}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Decay Threshold (%)</label>
              <input
                type="number"
                min="0.5"
                max="10"
                step="0.1"
                value={decayThreshold}
                onChange={(e) => setDecayThreshold(parseFloat(e.target.value) || 2.0)}
                className="form-control"
              />
              <small className="form-text text-muted">
                Repairs trigger when decay reaches this threshold (default: 2%, range: 0.5%-10%+)
              </small>
            </div>

            <div className="form-group">
              <label>Max Consecutive Touch-ups</label>
              <input
                type="number"
                min="1"
                max="10"
                value={maxConsecutiveTouchUps}
                onChange={(e) => setMaxConsecutiveTouchUps(parseInt(e.target.value) || 2)}
                className="form-control"
              />
              <small className="form-text text-muted">
                Maximum number of consecutive touch-up repairs before requiring a full repair (default: 2)
              </small>
            </div>
          </div>

          <div className="form-group">
            <label>Damage Split (%)</label>
            <div className="damage-split-inputs">
              <div className="split-input">
                <label>Level A (Minor)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={scenario.damageSplit.A}
                  onChange={(e) => handleDamageSplitChange('A', e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="split-input">
                <label>Level B (Medium)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={scenario.damageSplit.B}
                  onChange={(e) => handleDamageSplitChange('B', e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="split-input">
                <label>Level C (Severe)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={scenario.damageSplit.C}
                  onChange={(e) => handleDamageSplitChange('C', e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="split-total">
                Total: {scenario.damageSplit.A + scenario.damageSplit.B + scenario.damageSplit.C}%
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={useCustomRepairSteps}
                onChange={(e) => setUseCustomRepairSteps(e.target.checked)}
              />
              <span>Use Custom Repair Steps (from span configuration)</span>
            </label>
            <p className="help-text">
              {useCustomRepairSteps 
                ? 'Using repair steps defined in each span\'s configuration' 
                : 'Using rule-based repair steps from corrosion/damage combinations'}
            </p>
          </div>

          {/* Monte Carlo Configuration */}
          <div className="form-group mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
            <label className="checkbox-label mb-3">
              <input
                type="checkbox"
                checked={useMonteCarloMode}
                onChange={(e) => setUseMonteCarloMode(e.target.checked)}
                className="mr-2"
              />
              <span className="text-lg font-semibold text-purple-900">🎲 Enable Monte Carlo Simulation (Risk Analysis)</span>
            </label>
            <p className="help-text text-purple-700 mb-4">
              Run probabilistic simulations to analyze cost variability and budget risk. 
              {useMonteCarloMode 
                ? ' Monte Carlo mode enabled - will run multiple iterations with randomized parameters.' 
                : ' Enable to see confidence intervals and risk-weighted budgets.'}
            </p>

            {useMonteCarloMode && (
              <div className="space-y-4 mt-4 p-4 bg-white rounded-lg border border-purple-100">
                <h4 className="font-semibold text-gray-900 mb-3">Monte Carlo Parameters</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number of Iterations
                    </label>
                    <select
                      value={monteCarloConfig.iterations}
                      onChange={(e) => setMonteCarloConfig(prev => ({ ...prev, iterations: parseInt(e.target.value) }))}
                      className="form-control"
                    >
                      <option value="100">100 (Quick Test)</option>
                      <option value="500">500 (Fast)</option>
                      <option value="1000">1,000 (Standard)</option>
                      <option value="2000">2,000 (Detailed)</option>
                      <option value="5000">5,000 (High Precision)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">More iterations = more accurate but slower</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cost Variation (±%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="1"
                      value={monteCarloConfig.costVariation}
                      onChange={(e) => setMonteCarloConfig(prev => ({ ...prev, costVariation: parseFloat(e.target.value) }))}
                      className="form-control"
                    />
                    <p className="text-xs text-gray-500 mt-1">Typical: ±15% for materials & labor</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Degradation Variation (±%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="1"
                      value={monteCarloConfig.degradationVariation}
                      onChange={(e) => setMonteCarloConfig(prev => ({ ...prev, degradationVariation: parseFloat(e.target.value) }))}
                      className="form-control"
                    />
                    <p className="text-xs text-gray-500 mt-1">Coating life uncertainty: ±20%</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Inflation Rate Variation (±%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      step="0.1"
                      value={monteCarloConfig.discountRateVariation}
                      onChange={(e) => setMonteCarloConfig(prev => ({ ...prev, discountRateVariation: parseFloat(e.target.value) }))}
                      className="form-control"
                    />
                    <p className="text-xs text-gray-500 mt-1">Economic uncertainty: ±1%</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Repair Trigger Threshold (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={monteCarloConfig.repairThreshold * 100}
                      onChange={(e) => setMonteCarloConfig(prev => ({ ...prev, repairThreshold: parseFloat(e.target.value) / 100 }))}
                      className="form-control"
                    />
                    <p className="text-xs text-gray-500 mt-1">Trigger full repair at this degradation %</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maintenance Trigger Threshold (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={monteCarloConfig.maintenanceThreshold * 100}
                      onChange={(e) => setMonteCarloConfig(prev => ({ ...prev, maintenanceThreshold: parseFloat(e.target.value) / 100 }))}
                      className="form-control"
                    />
                    <p className="text-xs text-gray-500 mt-1">Trigger maintenance at this degradation %</p>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                  <strong>📊 Monte Carlo Analysis:</strong> Will run {monteCarloConfig.iterations.toLocaleString()} simulations 
                  with randomized parameters to generate cost distribution curves and confidence intervals (P10, P50, P90).
                  {selectedSpanIds.length > 5 && monteCarloConfig.iterations >= 2000 && (
                    <span className="block mt-2 text-yellow-700">
                      ⚠️ Warning: High iteration count with {selectedSpanIds.length} spans may take 2-5 minutes.
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              onClick={runSimulation}
              disabled={loading || selectedSpanIds.length === 0}
              className="btn btn-primary btn-large"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Running Simulation for {selectedSpanIds.length} spans... Please wait
                  {selectedSpanIds.length > 100 && ' (This may take 1-3 minutes)'}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlay} />
                  Run Simulation ({selectedSpanIds.length} span{selectedSpanIds.length !== 1 ? 's' : ''})
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Calculation Details - Positioned after Step 3 */}
      {selectedBridgeId && selectedSpanIds.length > 0 && (
        <div className="section-card">
          <div className="section-header">
            <div className="header-left">
              <button 
                className="collapse-btn"
                onClick={() => setIsCalculationDetailsCollapsed(!isCalculationDetailsCollapsed)}
                title={isCalculationDetailsCollapsed ? 'Show calculation details' : 'Hide calculation details'}
              >
                <FontAwesomeIcon icon={isCalculationDetailsCollapsed ? faChevronRight : faChevronDown} />
              </button>
              <h2>Calculation Details</h2>
            </div>
          </div>
          
          {!isCalculationDetailsCollapsed && (
            <div className="calculation-details">
              <div className="formula-section">
                <h3>Degradation Formula</h3>
                <p>Exponential growth is used to calculate coating failure based on provided degradation charts.</p>
                <div className="formula-box">
                  <strong>D(t) = A × e^(B × t)</strong>
                </div>
                <div className="variables">
                  <p><strong>A:</strong> coefficient</p>
                  <p><strong>B:</strong> growth rate</p>
                  <p><strong>t:</strong> years since last paint</p>
                </div>
              <div className="parameters-table">
                <h4>Parameters by Corrosion Category:</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>A (coefficient)</th>
                      <th>B (growth rate)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>C2</td><td>0.0098</td><td>0.2382</td></tr>
                    <tr><td>C3</td><td>0.0098</td><td>0.3454</td></tr>
                    <tr><td>C4</td><td>0.0111</td><td>0.4273</td></tr>
                    <tr><td>C5</td><td>0.0098</td><td>0.4934</td></tr>
                  </tbody>
                </table>
              </div>
              
              </div>

              <div className="formula-section">
                <h3>Area Calculation</h3>
                <div className="formula-box">
                  <strong>Degraded Area = Surface Area × Confined Space Multiplier × Degradation %</strong>
                </div>
                <p>The calculated degraded area is then split into severity levels A/B/C based on damage distribution.</p>
              </div>

              <div className="formula-section">
                <h3>Cost Calculation</h3>
                <div className="formula-box">
                  <strong>Total Cost = ∑(Area × Unit Cost)</strong>
                </div>
                <p>Included costs: Washing + Surface Prep + Materials (Prime/Stripe/Midcoat/Topcoat) + Labor</p>
                <p><em>Note: Mobilization costs are not currently included in the calculation</em></p>
              </div>

              <div className="formula-section">
                <h3>Present Value Calculation</h3>
                <div className="formula-box">
                  <strong>Present Value = ∑(Yearly Cost / (1 + discount_rate)^year)</strong>
                </div>
                <p>Converts future maintenance costs to today's value using discount rate for time value of money.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Simulation Results */}
      {simulationResults && (
        <div id="simulation-results" className="section-card results-section">
          <div className="results-header">
            <h2>
              <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
              Simulation Results
              {simulationResults.mode === 'monte_carlo' && (
                <span className="ml-3 px-3 py-1 bg-purple-600 text-white text-sm rounded-full">
                  🎲 Monte Carlo
                </span>
              )}
            </h2>
            <div className="results-meta">
              <span>Simulation ID: {simulationResults.simulationId}</span>
              <span>•</span>
              <span>{simulationResults.spans.length} spans</span>
              <span>•</span>
              <span>{simulationResults.timeHorizonYears} years</span>
              {simulationResults.mode === 'monte_carlo' && (
                <>
                  <span>•</span>
                  <span>{simulationResults.iterations.toLocaleString()} iterations</span>
                </>
              )}
            </div>
          </div>

          {/* Monte Carlo Results (if applicable) */}
          {simulationResults.mode === 'monte_carlo' && (
            <div className="mb-6">
              <MonteCarloResults results={simulationResults} />
            </div>
          )}

          {/* Summary Cards (deterministic only - Monte Carlo has its own detailed view above) */}
          {simulationResults.mode !== 'monte_carlo' && (
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-label">Total Cost (Nominal)</div>
                <div className="card-value">{formatCurrency(simulationResults.summary.totalCost)}</div>
              </div>
              <div className="summary-card highlight">
                <div className="card-label">Inflation-Adjusted Total</div>
                <div className="card-value">{formatCurrency(simulationResults.summary.npv)}</div>
              </div>
              <div className="summary-card">
                <div className="card-label">Average Yearly Cost</div>
                <div className="card-value">{formatCurrency(simulationResults.summary.avgYearlyCost)}</div>
              </div>
              <div className="summary-card">
                <div className="card-label">Peak Year</div>
                <div className="card-value">
                  Year {simulationResults.summary.peakYear}
                  <div className="card-subtitle">{formatCurrency(simulationResults.summary.peakCost)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Degradation Curves Chart */}
          {selectedSpanIds.length > 0 && (
            <div className="degradation-chart-section">
              <h3 style={{ color: '#3b82f6', fontSize: '1.5rem', marginBottom: '1rem', borderBottom: '2px solid #3b82f6', paddingBottom: '0.5rem' }}>
                📊 Degradation Curves
              </h3>
              <div className="chart-container">
                <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f0f8ff', border: '1px solid #3b82f6', borderRadius: '4px' }}>
                  <strong>Chart Status:</strong> Showing {spansToVisualize.length} of {selectedSpanIds.length} selected spans
                  {selectedSpanIds.length > MAX_CHART_SPANS && (
                    <span style={{ color: '#f59e0b', marginLeft: '10px' }}>
                      ⚠️ Displaying first {MAX_CHART_SPANS} spans for readability. All {selectedSpanIds.length} spans will be included in simulation.
                    </span>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="year" 
                      label={{ value: 'Years', position: 'insideBottom', offset: -10 }}
                    />
                    <YAxis 
                      label={{ value: '% Failure', angle: -90, position: 'insideLeft' }}
                      domain={[0, 12]}
                    />
                    <Tooltip 
                      formatter={(value, name) => [`${value.toFixed(2)}%`, name]}
                      labelFormatter={(label) => `Year ${label}`}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px' }}
                      iconSize={10}
                    />
                    {spansToVisualize.slice(0, MAX_CHART_SPANS).map((spanId, index) => {
                      const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#14b8a6'];
                      const color = colors[index % colors.length];
                      
                      return (
                        <Line
                          key={spanId}
                          type="monotone"
                          dataKey={`span_${spanId}`}
                          stroke={color}
                          strokeWidth={2}
                          name={`Element ${spanId}`}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Collapsible Yearly Breakdown Table */}
          <div className="results-table-section">
            <div className="section-header">
              <div className="header-left">
                <button
                  type="button"
                  className="collapse-btn"
                  onClick={() => setIsYearlyBreakdownCollapsed(!isYearlyBreakdownCollapsed)}
                  title={isYearlyBreakdownCollapsed ? 'Expand yearly breakdown' : 'Collapse yearly breakdown'}
                >
                  {isYearlyBreakdownCollapsed ? '▶' : '▼'}
                </button>
                <h3>📊 Yearly Cost Breakdown ({simulationResults.yearlyResults.length} years)</h3>
                {isYearlyBreakdownCollapsed && (
                  <span className="collapsed-summary">
                    Total: {formatCurrency(simulationResults.summary.totalCost)} | 
                    Inflated: {formatCurrency(simulationResults.summary.npv)}
                  </span>
                )}
              </div>
            </div>
            {!isYearlyBreakdownCollapsed && (
              <div className="table-container">
                <table className="results-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th className="text-right">Total Cost</th>
                      <th className="text-right">Inflation Factor</th>
                      <th className="text-right">Inflation-Adjusted Cost</th>
                      <th className="text-right">Spans Affected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {simulationResults.yearlyResults
                      .filter(yearResult => yearResult.totalCost > 0) // Hide zero values
                      .map((yearResult, index) => {
                        // Apply inflation factor (discountRate is actually inflation rate)
                        const inflationFactor = Math.pow(1 + simulationResults.scenario.discountRate, index + 1);
                        const inflatedCost = yearResult.totalCost * inflationFactor;
                        const spansWithCost = yearResult.spanResults.filter(s => s.totalCost > 0).length;
                        
                        return (
                          <tr key={yearResult.year}>
                            <td>Year {yearResult.year}</td>
                            <td className="text-right">{formatCurrency(yearResult.totalCost)}</td>
                            <td className="text-right">{formatNumber(inflationFactor, 4)}</td>
                            <td className="text-right">{formatCurrency(inflatedCost)}</td>
                            <td className="text-right">{spansWithCost}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td className="text-right"><strong>{formatCurrency(simulationResults.summary.totalCost)}</strong></td>
                      <td className="text-right">-</td>
                      <td className="text-right"><strong>{formatCurrency(simulationResults.summary.npv)}</strong></td>
                      <td className="text-right">-</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Collapsible Span Details */}
          <div className="results-table-section">
            <h3>📊 Span Details ({simulationResults.spans.length} spans)</h3>
            <div className="span-details-container">
              {simulationResults.spans.map(span => {
                // Calculate total cost for this span across all years
                const spanTotalCost = simulationResults.yearlyResults.reduce((sum, yearResult) => {
                  const spanResult = yearResult.spanResults.find(s => s.spanId === span.id);
                  return sum + (spanResult?.totalCost || 0);
                }, 0);

                // Calculate inflation-adjusted total for this span
                const spanInflatedTotal = simulationResults.yearlyResults.reduce((sum, yearResult, index) => {
                  const spanResult = yearResult.spanResults.find(s => s.spanId === span.id);
                  const inflationFactor = Math.pow(1 + simulationResults.scenario.discountRate, index + 1);
                  return sum + ((spanResult?.totalCost || 0) * inflationFactor);
                }, 0);

                // Find year when this span needs repair (first year with cost > 0)
                const repairYear = simulationResults.yearlyResults.find(yearResult => {
                  const spanResult = yearResult.spanResults.find(s => s.spanId === span.id);
                  return spanResult && spanResult.totalCost > 0;
                });

                const isExpanded = isSpanExpanded(span.id);

                return (
                  <div key={span.id} className="span-detail-card">
                    {/* Span Summary Header */}
                    <div 
                      className="span-detail-header"
                      onClick={() => toggleSpanExpansion(span.id)}
                    >
                      <div className="span-detail-header-left">
                        <button 
                          type="button"
                          className="span-expand-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSpanExpansion(span.id);
                          }}
                        >
                          {isExpanded ? '▼' : '▶'}
                        </button>
                        <div className="span-basic-info">
                          <div className="span-name">
                            <strong>{span.spanCode}</strong>
                            <span className="span-subtitle">{span.spanName}</span>
                          </div>
                        </div>
                      </div>
                      <div className="span-detail-header-right">
                        <span className={`badge ${getCorrosionBadgeClass(span.combinationCode)}`}>
                          {span.combinationCode}
                        </span>
                        <span className="span-total-cost">{formatCurrency(spanTotalCost)}</span>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="span-detail-content">
                        <div className="span-detail-grid">
                          <div className="span-detail-item">
                            <label>Area</label>
                            <span>{formatNumber(span.totalArea)} m²</span>
                          </div>
                          <div className="span-detail-item">
                            <label>Corrosion Category</label>
                            <span className={`badge ${getCorrosionBadgeClass(span.combinationCode)}`}>
                              {span.combinationCode}
                            </span>
                          </div>
                          <div className="span-detail-item">
                            <label>Total Cost</label>
                            <span className="cost-value">{formatCurrency(spanTotalCost)}</span>
                          </div>
                          <div className="span-detail-item">
                            <label>Inflation-Adjusted Total</label>
                            <span className="cost-value">{formatCurrency(spanInflatedTotal)}</span>
                          </div>
                          <div className="span-detail-item">
                            <label>Years to Repair</label>
                            <span>{repairYear ? repairYear.year : 'No repair needed'}</span>
                          </div>
                          <div className="span-detail-item">
                            <label>Last Repair Year</label>
                            <span>{span.lastRepairYear || 'Unknown'}</span>
                          </div>
                        </div>
                        
                        {/* Cost breakdown over time */}
                        <div className="span-cost-breakdown">
                          <h4>Cost Timeline (Repairs Only)</h4>
                          <div className="cost-timeline">
                            {simulationResults.yearlyResults
                              .filter(yearResult => {
                                const spanResult = yearResult.spanResults.find(s => s.spanId === span.id);
                                return spanResult && spanResult.totalCost > 0; // Only show years with repairs
                              })
                              .map((yearResult, index) => {
                                const spanResult = yearResult.spanResults.find(s => s.spanId === span.id);
                                if (!spanResult || spanResult.totalCost <= 0) return null;
                                
                                // Apply inflation factor (discountRate is actually inflation rate)
                                const inflationFactor = Math.pow(1 + simulationResults.scenario.discountRate, index + 1);
                                const inflatedCost = spanResult.totalCost * inflationFactor;
                                
                                return (
                                  <div key={yearResult.year} className="cost-timeline-item">
                                    <span className="timeline-year">Year {yearResult.year}</span>
                                    <span className="timeline-cost">{formatCurrency(spanResult.totalCost)}</span>
                                    <span className="timeline-npv">Inflated: {formatCurrency(inflatedCost)}</span>
                                  </div>
                                );
                              })
                              .filter(Boolean) // Remove null entries
                            }
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpanSimulation;


